import { useEffect, useRef, useState } from 'react';

import { CandidateUI } from '@/api/matchmaker/types.ts';
import { ReactionsUpsaleCloudKeys } from '@/enums/storageEnums.ts';
import { useCloudStorage } from '@/hooks/useStorage.ts';

interface ReactionsUpsaleStorageData {
  firstOpenAt: number;
  lastShownAt: number | null;
}

const ONE_HOUR = 60 * 60 * 1000;
const TWO_DAYS = 2 * 24 * 60 * 60 * 1000;

export const useReactionsUpsale = (remainingAmount: number) => {
  const [reactionsUpsaleOpen, setReactionsUpsaleOpen] = useState(false);
  const [likedCandidate, setLikedCandidate] = useState<CandidateUI | null>(
    null
  );

  const storage = useCloudStorage();

  const canShowRef = useRef<boolean>(false);
  const dataRef = useRef<ReactionsUpsaleStorageData | null>(null);

  useEffect(() => {
    const initStorage = async () => {
      try {
        const data = await storage.readData<ReactionsUpsaleStorageData>(
          ReactionsUpsaleCloudKeys.REACTIONS_UPSALE_DATA
        );

        const now = Date.now();

        if (!data) {
          const newData: ReactionsUpsaleStorageData = {
            firstOpenAt: now,
            lastShownAt: null
          };

          storage.writeData(
            ReactionsUpsaleCloudKeys.REACTIONS_UPSALE_DATA,
            newData
          );

          dataRef.current = newData;
          canShowRef.current = false;
          return;
        }

        dataRef.current = data;

        const timeSinceFirstOpen = now - data.firstOpenAt;
        const passedRegistrationDelay = timeSinceFirstOpen >= ONE_HOUR;

        const passedIntervalDelay =
          data.lastShownAt === null || now - data.lastShownAt >= TWO_DAYS;

        canShowRef.current = passedRegistrationDelay && passedIntervalDelay;
      } catch (_) {
        canShowRef.current = true;
      }
    };

    void initStorage();
  }, []);

  const openReactionsUpsale = (candidate: CandidateUI) => {
    if (candidate.likesMe || !candidate.isActive || remainingAmount > 0) {
      return;
    }

    if (!canShowRef.current) {
      return;
    }

    setLikedCandidate(candidate);
    setReactionsUpsaleOpen(true);

    const now = Date.now();
    canShowRef.current = false;

    const newData: ReactionsUpsaleStorageData = {
      firstOpenAt: dataRef.current?.firstOpenAt ?? now,
      lastShownAt: now
    };

    storage.writeData(ReactionsUpsaleCloudKeys.REACTIONS_UPSALE_DATA, newData);

    dataRef.current = newData;
  };

  const closeReactionsUpsale = () => {
    setReactionsUpsaleOpen(false);
    setLikedCandidate(null);
  };

  return {
    reactionsUpsaleOpen,
    likedCandidate,
    openReactionsUpsale,
    closeReactionsUpsale
  };
};